import React from "react";
import { LayoutGrid, Timer, Calendar, Wallet, MoreHorizontal, Umbrella, Receipt, Settings, HelpCircle, Coffee, Square, Repeat } from "lucide-react";
import { BG_SOFT, TEXT_INK, TEXT_DIM, BORDER, BRAND, MINT, ORANGE, TEAL, RED } from "../tokens";
import { Legend, RoundButton } from "./helpers";

export function TrackTimeScreen() {
  return (
    <div style={{ padding: "8px 18px 14px", display: "flex", flexDirection: "column", gap: 12, height: "100%" }}>
      <div style={{ display: "flex", background: BG_SOFT, borderRadius: 8, padding: 3, fontSize: 11, fontWeight: 600 }}>
        <div style={{ flex: 1, padding: "6px 0", textAlign: "center", background: "#FFFFFF", borderRadius: 6, color: BRAND, boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>Time clock</div>
        <div style={{ flex: 1, padding: "6px 0", textAlign: "center", color: TEXT_DIM }}>Timesheets</div>
      </div>
      <div style={{ textAlign: "center", padding: "4px 0" }}>
        <div style={{ fontSize: 30, fontWeight: 800, color: TEXT_INK, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em" }}>1h 04m 05s</div>
        <div style={{ fontSize: 10, color: TEXT_DIM, marginTop: 2 }}>Start time: Today, 8:37 AM</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <div style={{ background: "#FFFFFF", borderRadius: 10, padding: "10px 12px", border: "0.5px solid " + BORDER }}>
          <div style={{ fontSize: 9, color: TEXT_DIM, fontWeight: 600 }}>Clocked in</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_INK, marginTop: 2 }}>8:00 AM</div>
        </div>
        <div style={{ background: "#FFFFFF", borderRadius: 10, padding: "10px 12px", border: "0.5px solid " + BORDER }}>
          <div style={{ fontSize: 9, color: TEXT_DIM, fontWeight: 600 }}>Added today</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_INK, marginTop: 2 }}>7h 30m</div>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-around", marginTop: "auto", paddingTop: 8 }}>
        <RoundButton icon={Coffee} label="Break" color={ORANGE} />
        <RoundButton icon={Square} label="Clock out" color={RED} />
        <RoundButton icon={Repeat} label="Switch" color={TEAL} />
      </div>
    </div>
  );
}

export function OverviewScreen() {
  const pct = 7.5 / 8;
  const r = 36;
  const c = 2 * Math.PI * r;
  return (
    <div style={{ padding: "8px 18px 14px", display: "flex", flexDirection: "column", gap: 11, height: "100%" }}>
      <div style={{ background: "#FFFFFF", border: "0.5px solid " + BORDER, borderRadius: 12, padding: 12, display: "flex", alignItems: "center", gap: 12 }}>
        <svg width={84} height={84} viewBox="-46 -46 92 92">
          <circle r={r} fill="none" stroke={BG_SOFT} strokeWidth="8" />
          <circle r={r} fill="none" stroke={BRAND} strokeWidth="8" strokeDasharray={c} strokeDashoffset={c * (1 - pct)} transform="rotate(-90)" strokeLinecap="round" />
          <text textAnchor="middle" dy=".3em" fontSize="13" fontWeight="700" fill={TEXT_INK}>7h 30m</text>
        </svg>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 10, color: TEXT_DIM, fontWeight: 600 }}>Day total</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_INK, marginTop: 2 }}>of 8 hours</div>
          <div style={{ fontSize: 9, color: TEXT_DIM, marginTop: 4 }}>Clocked in 8:00 AM</div>
        </div>
      </div>
      <div style={{ background: "#FFFFFF", border: "0.5px solid " + BORDER, borderRadius: 12, padding: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 9, color: TEXT_DIM, fontWeight: 600 }}>Latest pay</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: TEXT_INK, marginTop: 2, fontVariantNumeric: "tabular-nums" }}>$762.66</div>
            <div style={{ fontSize: 9, color: TEXT_DIM, marginTop: 2 }}>Paid Jun 13</div>
          </div>
          <span style={{ fontSize: 9, color: BRAND, fontWeight: 700 }}>View all</span>
        </div>
      </div>
      <div style={{ background: "#FFFFFF", border: "0.5px solid " + BORDER, borderRadius: 12, padding: 12 }}>
        <div style={{ fontSize: 9, color: TEXT_DIM, fontWeight: 600 }}>Next shift</div>
        <div style={{ fontSize: 12, fontWeight: 700, color: TEXT_INK, marginTop: 2 }}>Jun 16, 9:00 AM to 5:00 PM</div>
        <div style={{ fontSize: 9, color: TEXT_DIM, marginTop: 4 }}>Time off balance: 37.92 hrs</div>
      </div>
    </div>
  );
}

export function MoneyScreen() {
  const net = 10072 / 12680;
  const tax = 2488 / 12680;
  const ded = 120 / 12680;
  const r = 36;
  const c = 2 * Math.PI * r;
  return (
    <div style={{ padding: "8px 18px 14px", display: "flex", flexDirection: "column", gap: 11, height: "100%" }}>
      <div style={{ background: "#FFFFFF", border: "0.5px solid " + BORDER, borderRadius: 12, padding: 12, display: "flex", alignItems: "center", gap: 12 }}>
        <svg width={84} height={84} viewBox="-46 -46 92 92">
          <circle r={r} fill="none" stroke={BG_SOFT} strokeWidth="9" />
          <circle r={r} fill="none" stroke={BRAND} strokeWidth="9" strokeDasharray={c} strokeDashoffset={c * (1 - net)} transform="rotate(-90)" />
          <circle r={r} fill="none" stroke={MINT} strokeWidth="9" strokeDasharray={c} strokeDashoffset={c - (c * tax)} transform={"rotate(" + (-90 + 360 * net) + ")"} />
          <circle r={r} fill="none" stroke={ORANGE} strokeWidth="9" strokeDasharray={c} strokeDashoffset={c - (c * ded)} transform={"rotate(" + (-90 + 360 * (net + tax)) + ")"} />
          <text textAnchor="middle" dy="-.1em" fontSize="8" fill={TEXT_DIM}>Gross 2026</text>
          <text textAnchor="middle" dy="1em" fontSize="11" fontWeight="800" fill={TEXT_INK}>$12,560</text>
        </svg>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
          <Legend color={BRAND} label="Net pay" value="$10,072" />
          <Legend color={MINT} label="Tax withheld" value="$2,488" />
          <Legend color={ORANGE} label="Deductions" value="$120" />
        </div>
      </div>
      <div style={{ background: "#FFFFFF", border: "0.5px solid " + BORDER, borderRadius: 12, padding: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 9, color: TEXT_DIM, fontWeight: 600 }}>Latest pay</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: TEXT_INK, marginTop: 2 }}>$762.66</div>
            <div style={{ fontSize: 9, color: TEXT_DIM, marginTop: 2 }}>74 hours</div>
          </div>
          <span style={{ fontSize: 9, color: BRAND, fontWeight: 700 }}>View details</span>
        </div>
      </div>
    </div>
  );
}

interface PhoneMockProps {
  screen: React.ReactNode;
  activeTab: string;
  barVariant: "time" | "pay";
}

export default function PhoneMock({ screen, activeTab, barVariant }: PhoneMockProps) {
  const TIME_TABS = [
    { label: "Overview", icon: LayoutGrid },
    { label: "Track Time", icon: Timer },
    { label: "Schedule", icon: Calendar },
    { label: "Money", icon: Wallet },
    { label: "More", icon: MoreHorizontal },
  ];
  const PAY_TABS = [
    { label: "Money", icon: Wallet },
    { label: "Time Off", icon: Umbrella },
    { label: "Taxes", icon: Receipt },
    { label: "Settings", icon: Settings },
    { label: "Help", icon: HelpCircle },
  ];
  const tabs = barVariant === "pay" ? PAY_TABS : TIME_TABS;
  return (
    <div style={{
      width: 290, height: 580, background: "#1A2B2B", borderRadius: 36, padding: 8,
      boxShadow: "0 30px 60px rgba(15, 149, 153, 0.18)", position: "relative",
    }}>
      <div style={{ position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)", width: 110, height: 22, background: "#1A2B2B", borderRadius: 12, zIndex: 2 }} />
      <div style={{ background: "#F7F9F9", height: "100%", borderRadius: 30, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <div style={{ height: 38, padding: "10px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12, fontWeight: 700, color: TEXT_INK }}>
          <span>9:41</span>
          <span style={{ display: "inline-flex", gap: 5, alignItems: "center" }}>
            <svg width={14} height={9} viewBox="0 0 14 9"><rect x="0" y="6" width="2" height="3" fill={TEXT_INK} /><rect x="3" y="4" width="2" height="5" fill={TEXT_INK} /><rect x="6" y="2" width="2" height="7" fill={TEXT_INK} /><rect x="9" y="0" width="2" height="9" fill={TEXT_INK} /></svg>
            <span style={{ display: "inline-block", width: 22, height: 10, border: "1px solid " + TEXT_INK, borderRadius: 2, position: "relative" }}>
              <span style={{ position: "absolute", inset: 1, background: TEXT_INK, borderRadius: 1, width: "70%" }} />
            </span>
          </span>
        </div>
        <div style={{ flex: 1, overflow: "hidden" }}>{screen}</div>
        <div style={{ display: "flex", justifyContent: "space-around", padding: "8px 6px 14px", background: "#FFFFFF", borderTop: "0.5px solid " + BORDER }}>
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = t.label === activeTab;
            return (
              <div key={t.label} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, color: active ? BRAND : "#9CA3AF" }}>
                <Icon size={18} />
                <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: "0.01em" }}>{t.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}